// src/App.tsx
import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence } from 'framer-motion';
import { Header } from './components/layout/Header';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import { CacheProvider } from './contexts/CacheContext';
import { CookieConsentBanner } from './components/layout/CookieConsentBanner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toaster } from 'react-hot-toast';
import { useHeartbeat } from './hooks/useHeartbeat';
import { DiscountPromoModal } from './components/payment/DiscountPromoModal';
import { MobileBottomNav } from './components/layout/MobileBottomNav';
import { useState } from 'react';
import { useAuth } from './hooks/useAuth';
import { OnboardingModal } from './components/onboarding/OnboardingModal';


// Lazy loading de páginas
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const HomePage = lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const MatchesPage = lazy(() => import('./pages/MatchesPage').then(m => ({ default: m.MatchesPage })));
const PredictionsPage = lazy(() => import('./pages/PredictionsPage').then(m => ({ default: m.PredictionsPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminSyncPage = lazy(() => import('./pages/AdminSyncPage').then(m => ({ default: m.AdminSyncPage })));
const PredictionsComparisonPage = lazy(() => import('./pages/PredictionsComparisonPage').then(m => ({ default: m.PredictionsComparisonPage })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage').then(m => ({ default: m.UserManagementPage })));
const ModelComparisonPage = lazy(() => import('./pages/ModelComparisonPage').then(m => ({ default: m.ModelComparisonPage })));
const TicketsManagementPage = lazy(() => import('./pages/TicketsManagementPage').then(m => ({ default: m.TicketsManagementPage })));
const ActiveUsersPage = lazy(() => import('./pages/ActiveUsersPage').then(m => ({ default: m.ActiveUsersPage })));
const MyBetsPage = lazy(() => import('./pages/MyBetsPage').then(m => ({ default: m.MyBetsPage })));
const AdminPromoPage = lazy(() => import('./pages/AdminPromoPage').then(m => ({ default: m.AdminPromoPage })));
const AdminParlaySimulatorPage = lazy(() => import('./pages/AdminParlaySimulatorPage').then(m => ({ default: m.AdminParlaySimulatorPage })));
const AdminDiscountPage = lazy(() => import('./pages/AdminDiscountPage').then(m => ({ default: m.AdminDiscountPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
const PaymentFailurePage = lazy(() => import('./pages/PaymentFailurePage').then(m => ({ default: m.PaymentFailurePage })));
const PaddleCheckoutPage = lazy(() => import('./pages/PaddleCheckoutPage').then(m => ({ default: m.PaddleCheckoutPage })));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage').then(m => ({ default: m.ChangePasswordPage })));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage').then(m => ({ default: m.FavoritesPage })));

// Heartbeat: tracks real user activity for the admin active-users page
function Heartbeat() {
  useHeartbeat();
  return null;
}

// Auto-shows OnboardingModal for users who haven't completed onboarding
function OnboardingTrigger() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const shouldShow =
    !dismissed &&
    !!user &&
    !user.user_metadata?.onboarding_complete &&
    sessionStorage.getItem('show_onboarding') !== '1';

  if (!shouldShow) return null;

  return <OnboardingModal onComplete={() => setDismissed(true)} />;
}

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-slate-400">Cargando...</p>
    </div>
  </div>
);

// AnimatedRoutes component with location-based transitions
function AnimatedRoutes() {
  const location = useLocation();

  return (
    <>
      <Heartbeat />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* Payment Result Pages */}
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/failure" element={<PaymentFailurePage />} />
            <Route
              path="/paddle/checkout"
              element={
                <ProtectedRoute>
                  <PaddleCheckoutPage />
                </ProtectedRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/matches"
              element={
                <ProtectedRoute>
                  <MatchesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/predictions"
              element={
                <ProtectedRoute>
                  <PredictionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-bets"
              element={
                <ProtectedRoute>
                  <MyBetsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/favorites"
              element={
                <ProtectedRoute>
                  <FavoritesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <UserManagementPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/active-users"
              element={
                <AdminRoute>
                  <ActiveUsersPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/sync"
              element={
                <AdminRoute>
                  <AdminSyncPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/comparison"
              element={
                <AdminRoute>
                  <PredictionsComparisonPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/model-comparison"
              element={
                <AdminRoute>
                  <ModelComparisonPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/tickets"
              element={
                <AdminRoute>
                  <TicketsManagementPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/promo"
              element={
                <AdminRoute>
                  <AdminPromoPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/parlay-simulator"
              element={
                <AdminRoute>
                  <AdminParlaySimulatorPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/discounts"
              element={
                <AdminRoute>
                  <AdminDiscountPage />
                </AdminRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AnimatePresence>
      <MobileBottomNav />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <CacheProvider>
          <BrowserRouter>
            <div className="min-h-screen bg-slate-950 flex flex-col">
              <Header />
              <Suspense fallback={<LoadingScreen />}>
                <div className="flex-1 min-h-0 flex flex-col pb-16 md:pb-0">
                  <AnimatedRoutes />
                </div>
              </Suspense>
              <DiscountPromoModal />
              <OnboardingTrigger />
              <CookieConsentBanner />
              <Toaster
                position="bottom-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#131e3e',
                    color: '#fff',
                    border: '1px solid rgba(33, 78, 128, 0.5)',
                  },
                  success: {
                    iconTheme: { primary: '#10b981', secondary: '#fff' },
                  },
                  error: {
                    iconTheme: { primary: '#ef4444', secondary: '#fff' },
                  },
                }}
              />
            </div>
          </BrowserRouter>
        </CacheProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;